import React from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import { AdaptedRow, BasicTableProps } from './Types'
import { Box, Fade, TableFooter, TablePagination, TableHead, TableRow, TableCell } from '@mui/material'
import { CampaignType } from 'graphql/generated/graphql'
import CampaignTableRow from './CampaignTableRow'
import { BarChart3 } from 'lucide-react'

function adaptData(
  campaignId: string,
  name: string,
  createdAt: number,
  keywords: string[],
  platforms: string[],
  hitsCount: number,
  type: CampaignType
): AdaptedRow {
  return {
    campaignId,
    name,
    createdAt: new Date(createdAt).toDateString(),
    keywords: keywords.join(', '),
    platforms: platforms.join(', '),
    hitsCount,
    type,
  }
}

export default function BasicTable({
  rows,
  filterData,
  searchString,
}: BasicTableProps): JSX.Element {
  const [checked] = React.useState(true)
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(5)

  React.useEffect(() => {
    setPage(0)
  }, [rows, filterData, searchString])

  const adaptedRows = rows.map((r) =>
    adaptData(
      r.campaignId || '',
      r.name || '',
      r.createdAt || 0,
      r.keywords || [],
      r.platforms || [],
      r.hitsCount || 0,
      r.type ?? ''
    )
  )

  const paginatedRows =
    rowsPerPage > 0
      ? adaptedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : adaptedRows

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <Fade in={checked} timeout={1500}>
      <Box
        sx={{
          background: 'rgba(30, 41, 59, 0.5)',
          backdropFilter: 'blur(24px)',
          borderRadius: '16px',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Table Header */}
        <Box
          sx={{
            padding: '24px',
            background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(236, 72, 153, 0.15) 100%)',
            borderBottom: '1px solid rgba(168, 85, 247, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(147, 51, 234, 0.4)',
              }}
            >
              <BarChart3 style={{ width: '24px', height: '24px', color: 'white' }} />
            </Box>
            <Box>
              <Box
                sx={{
                  fontSize: '24px',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '4px',
                }}
              >
                Campaigns
              </Box>
              <Box sx={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.5)' }}>
                {adaptedRows.length} total campaign{adaptedRows.length !== 1 ? 's' : ''}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Table Content */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            {/* Table Head */}
            <TableHead
              sx={{
                background: 'rgba(15, 23, 42, 0.5)',
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'rgba(168, 85, 247, 0.7)',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    border: 'none',
                    padding: '16px 24px',
                  }}
                >
                  Campaign Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'rgba(168, 85, 247, 0.7)',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    border: 'none',
                    padding: '16px 24px',
                  }}
                >
                  Campaign Type
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'rgba(168, 85, 247, 0.7)',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    border: 'none',
                    padding: '16px 24px',
                  }}
                >
                  Keywords
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'rgba(168, 85, 247, 0.7)',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    border: 'none',
                    padding: '16px 24px',
                  }}
                >
                  Hits Count
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'rgba(168, 85, 247, 0.7)',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    border: 'none',
                    padding: '16px 24px',
                  }}
                >
                  Platforms
                </TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body */}
            <TableBody>
              {paginatedRows.map(
                ({ campaignId, hitsCount, name, keywords, platforms, type }) => {
                  return (
                    <CampaignTableRow
                      key={campaignId}
                      campaignId={campaignId}
                      hitsCount={hitsCount}
                      name={name}
                      keywords={keywords}
                      platforms={platforms}
                      type={type}
                      filterData={filterData}
                    />
                  )
                }
              )}
            </TableBody>

            {/* Table Footer with Pagination */}
            <TableFooter
              sx={{
                background: 'rgba(15, 23, 42, 0.5)',
                '& .MuiTablePagination-toolbar': {
                  color: '#e0e0e0',
                  padding: '16px 24px',
                },
                '& .MuiTablePagination-selectLabel': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiTablePagination-selectIcon': {
                  color: 'rgba(168, 85, 247, 0.7)',
                },
                '& .MuiSelect-select': {
                  color: '#e0e0e0',
                  backgroundColor: 'rgba(30, 41, 59, 0.5)',
                  borderRadius: '8px',
                  padding: '8px 32px 8px 12px',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(30, 41, 59, 0.8)',
                    borderColor: 'rgba(168, 85, 247, 0.4)',
                  },
                },
                '& .MuiTablePagination-displayedRows': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiIconButton-root': {
                  color: 'rgba(168, 85, 247, 0.7)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(168, 85, 247, 0.1)',
                    color: '#a855f7',
                    transform: 'scale(1.1)',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(224, 224, 224, 0.2)',
                  },
                },
                '& .MuiSelect-icon': {
                  color: 'rgba(168, 85, 247, 0.7)',
                },
              }}
            >
              <TableRow sx={{ border: 'none' }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={6}
                  count={adaptedRows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ border: 'none' }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>

        {/* Empty State */}
        {adaptedRows.length === 0 && (
          <Box
            sx={{
              padding: '80px 24px',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: '80px',
                height: '80px',
                margin: '0 auto 20px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BarChart3 style={{ width: '40px', height: '40px', color: '#a855f7' }} />
            </Box>
            <Box
              sx={{
                fontSize: '20px',
                fontWeight: 600,
                color: 'white',
                marginBottom: '8px',
              }}
            >
              No campaigns yet
            </Box>
            <Box sx={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.5)' }}>
              Create your first campaign to get started
            </Box>
          </Box>
        )}
      </Box>
    </Fade>
  )
}
